import { configureDarkTheme } from './dark';
import { configureLightTheme } from './light';

export function setupCustomThemesForAce(ace: AceAjax.Ace): void {
  configureDarkTheme(ace);
  configureLightTheme(ace);
}

/*
Utility functions for Ace Editor
*/

export function ensureTokensHaveIndexAndStart(tokens: AceAjax.Token[]): AceAjax.Token[] {
  let currentStart = 0;
  return tokens.map((token, index) => {
    const start = currentStart;
    currentStart += token.value.length;
    return {
      value: token.value,
      type: token.type,
      index: index,
      start: start,
    };
  });
}

export function getCurrentCodeLine(editor: AceAjax.Editor): string {
  const cursor = editor.getCursorPosition();
  const session = editor.getSession();
  const line = session.getLine(cursor.row);
  return line;
}

export function getTokensAtLine(line: number, editor: AceAjax.Editor): AceAjax.Token[] {
  const session = editor.getSession();
  const tokens = session.getTokens(line) as AceAjax.Token[];
  return ensureTokensHaveIndexAndStart(tokens);
}

export function getCurrentLineTokens(editor: AceAjax.Editor): AceAjax.Token[] {
  const { row } = editor.getCursorPosition();
  return getTokensAtLine(row, editor);
}

export function getCurrentCodeLineAsArray(editor: AceAjax.Editor): string[] {
  return getCurrentLineTokens(editor).map((token) => token.value);
}

export function getCurrentCursorPosition(editor: AceAjax.Editor): AceAjax.Position {
  return editor.getCursorPosition();
}

export function getTokenAtCurrentCursorPosition(editor: AceAjax.Editor): AceAjax.Token | null {
  const position = getCurrentCursorPosition(editor);
  return getTokenAt(position, editor);
}

// Same implementation of getTokenAt in AceEditor, but using the custom getTokensAtLine function
export function getTokenAt(position: AceAjax.Position, editor: AceAjax.Editor): AceAjax.Token | null {
  const { row, column } = position;
  const tokens = getTokensAtLine(row, editor);
  let token: AceAjax.Token | null = null;
  let cummulativeLength = 0;
  let i: number;

  if (column == null) {
    i = tokens.length - 1;
    cummulativeLength = editor.getLine(row).length;
  } else {
    for (i = 0; i < tokens.length; i++) {
      cummulativeLength += tokens[i].value.length;
      if (cummulativeLength >= column) break;
    }
  }
  token = tokens[i];
  if (!token) return null;
  token.index = i;
  token.start = cummulativeLength - token.value.length;
  return token;
}

export function getTokensAroundCursor(editor: AceAjax.Editor): {
  tokenBeforeCursor: AceAjax.Token | null;
  tokenAtCursor: AceAjax.Token | null;
  tokenAfterCursor: AceAjax.Token | null;
} {
  const position = getCurrentCursorPosition(editor);
  const { row } = position;
  const tokens = getTokensAtLine(row, editor);
  const tokenAtCursor = getTokenAt(position, editor);
  if (!tokenAtCursor) {
    return {
      tokenBeforeCursor: null,
      tokenAtCursor: null,
      tokenAfterCursor: null,
    };
  }
  const tokenBeforeCursor = tokens[tokenAtCursor.index - 1] ?? null;
  const tokenAfterCursor = tokens[tokenAtCursor.index + 1] ?? null;
  return {
    tokenBeforeCursor,
    tokenAtCursor,
    tokenAfterCursor,
  };
}

export function getCodeAroundCursor(editor: AceAjax.Editor) {
  const session = editor.getSession();
  const document = session.getDocument();
  const cursorPosition = editor.getCursorPosition();

  if (cursorPosition.row === 0 && cursorPosition.column === 0) {
    return {
      textFromStartToCurrent: '',
      textFromCurrentToEnd: '',
    };
  }

  const textFromStartToCurrent = document.getTextRange({
    start: { row: 0, column: 0 },
    end: cursorPosition,
  });

  const endRow = document.getLength() - 1;
  const endColumn = session.getLine(endRow).length;
  const textFromCurrentToEnd = document.getTextRange({
    start: cursorPosition,
    end: { row: endRow, column: endColumn },
  });

  return {
    textFromStartToCurrent,
    textFromCurrentToEnd,
  };
}
getCodeAroundCursor;
export function getLinePrefixBeforeCursor(editor: AceAjax.Editor) {
  const cursor = getCurrentCursorPosition(editor);
  const line = getCurrentCodeLine(editor);
  return line.substring(0, cursor.column);
}

export function getLineSuffixAfterCursor(editor: AceAjax.Editor) {
  const cursor = getCurrentCursorPosition(editor);
  const line = getCurrentCodeLine(editor);
  return line.substring(cursor.column);
}

export function isStartOfNewCell(editor: AceAjax.Editor): boolean {
  const cursor = editor.getCursorPosition();
  return cursor.row === 0;
}
